import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { WifiOff, RefreshCw, LogIn, AlertTriangle } from 'lucide-react';
import { PATHS } from '../router/paths';

/**
 * The screen shown when a protected route cannot be entered yet.
 *
 * It used to say "No connection … You're still signed in" for every one of those
 * situations, which was actively misleading in the common one: an expired
 * session on a perfectly good network produced a connection error for a user who
 * was online and NOT signed in. Someone told to check their Wi-Fi will check
 * their Wi-Fi, find nothing wrong, and be stuck.
 *
 * Each variant now says what actually happened and offers the action that
 * resolves it:
 *
 *   offline  — the device reports no network. Reloading is the fix, and it
 *              happens automatically the moment connectivity returns.
 *   session  — online, but the session could not be confirmed. Retries by
 *              default: this is usually auth being slow to restore rather than
 *              a real logout, and pushing this app's users to re-authenticate
 *              is genuinely dangerous — almost all of them sign in with a
 *              username against a synthetic @temp.afroyap.com address and have
 *              no phone, so a password reset goes to an undeliverable inbox and
 *              a misremembered password is a permanent lockout. Signing in is
 *              offered, quietly, for whoever actually needs it.
 *   error    — online and signed in, but the account failed to load. Neither
 *              a network nor an auth problem, so it says so rather than
 *              blaming one of them.
 *
 * Styling note: padding, weight and z-index are inline because px-8, font-bold
 * and z-[99999] have no rule in the committed pre-compiled index.css. The
 * missing padding is why the icon and label sat flush against the pill's edge.
 */
export type ConnectionVariant = 'offline' | 'session' | 'error';

interface OfflineScreenProps {
    variant?: ConnectionVariant;
}

const COPY: Record<ConnectionVariant, {
    Icon: typeof WifiOff;
    tint: string;
    title: string;
    body: string;
    primary: string;
}> = {
    offline: {
        Icon: WifiOff,
        tint: '#E74C3C',
        title: 'No connection',
        body: "We can't reach the network right now. Check your Wi-Fi or mobile data, or switch networks, then try again. You're still signed in.",
        primary: 'Try again',
    },
    session: {
        Icon: RefreshCw,
        tint: '#4478ea',
        title: 'Reconnecting you',
        body: "We couldn't confirm your session just now. You're still signed in on this device, and this usually clears on its own.",
        primary: 'Try again',
    },
    error: {
        Icon: AlertTriangle,
        tint: '#F39C12',
        title: "Couldn't load your account",
        body: "You're online and signed in, but your profile didn't finish loading. This is usually temporary.",
        primary: 'Try again',
    },
};

export function OfflineScreen({ variant = 'offline' }: OfflineScreenProps) {
    const [busy, setBusy] = useState(false);
    const navigate = useNavigate();
    const { Icon, tint, title, body, primary } = COPY[variant];

    // Only meaningful for the offline variant: the other two are not waiting on
    // the network, and reloading them on a connectivity blip would be noise.
    useEffect(() => {
        if (variant !== 'offline') return;
        const onOnline = () => window.location.reload();
        window.addEventListener('online', onOnline);
        return () => window.removeEventListener('online', onOnline);
    }, [variant]);

    // Reloading is the primary action for EVERY variant, including session.
    //
    // An unconfirmed session is usually auth being slow to restore, not a real
    // logout, and this app cannot afford to treat the two alike: roughly all of
    // its accounts sign in with a username against a synthetic
    // @temp.afroyap.com address and have no phone number, so a password reset
    // mails an undeliverable inbox. Anyone who re-authenticates and misremembers
    // their password is locked out permanently, with no self-service recovery.
    // So the screen retries by default and keeps signing in behind a second,
    // deliberate tap.
    const act = () => {
        setBusy(true);
        window.location.reload();
    };

    // Client-side routing, not window.location: in the native WebView there is
    // no server to serve /login, so a hard navigation would land on nothing.
    const signIn = () => {
        setBusy(true);
        navigate(PATHS.LOGIN, { replace: true });
    };

    return (
        <div
            className="fixed inset-0 flex flex-col items-center justify-center text-center"
            style={{ background: '#0D0D0D', zIndex: 99999, paddingLeft: 32, paddingRight: 32 }}
        >
            <div
                className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
                style={{ background: `${tint}1F` }}
            >
                <Icon style={{ width: 38, height: 38, color: tint }} />
            </div>

            <h1 className="text-2xl mb-2" style={{ color: '#ffffff', fontWeight: 700 }}>
                {title}
            </h1>
            <p className="text-sm mb-8" style={{ color: 'rgba(255,255,255,0.6)', maxWidth: 300 }}>
                {body}
            </p>

            <button
                onClick={act}
                disabled={busy}
                className="inline-flex items-center justify-center gap-2 h-12 rounded-full text-white"
                style={{
                    background: 'linear-gradient(135deg, #4478ea 0%, #2d1ef1 100%)',
                    fontWeight: 700,
                    paddingLeft: 32,
                    paddingRight: 32,
                    opacity: busy ? 0.6 : 1,
                }}
            >
                <RefreshCw style={{ width: 18, height: 18 }} />
                {busy ? 'One moment…' : primary}
            </button>

            {/* Deliberately quiet, and deliberately last. Someone who genuinely
                was signed out needs this; someone whose auth was merely slow
                should reload instead, because for most accounts here a
                half-remembered password is a permanent lockout. */}
            {variant === 'session' && !busy && (
                <button
                    onClick={signIn}
                    className="mt-6 text-sm inline-flex items-center gap-1.5"
                    style={{ color: 'rgba(255,255,255,0.45)' }}
                >
                    <LogIn style={{ width: 14, height: 14 }} />
                    Sign in again
                </button>
            )}
        </div>
    );
}

export default OfflineScreen;
